import { useEffect, useState } from 'react'
import requestService from '@/services/request'

const listeners = new Set()

const INITIAL = {
  tradingHalted: false,
  level: 'open',
  scope: 'site',
  audience: 'public',
  message: '',
  applies: false,
}

function derive(raw) {
  return {
    ...raw,
    tradingPaused: Boolean(raw.tradingHalted) || (raw.level === 'trading' && raw.applies),
    closed: raw.level === 'site' && raw.applies,
  }
}

let snapshot = derive(INITIAL)
let requestId = 0

function publish(next) {
  snapshot = derive(next)
  listeners.forEach(listener => listener(snapshot))
}

function normalize(data) {
  const site = data?.site_control || {}
  return {
    tradingHalted: Boolean(data?.trading_halted),
    level: site.level || 'open',
    scope: site.scope || 'site',
    audience: site.audience || 'public',
    message: site.message || '',
    applies: Boolean(site.applies),
  }
}

export function siteStatus() {
  return snapshot
}

export function isTradingPaused() {
  return snapshot.tradingPaused
}

export async function refreshSiteStatus() {
  if (typeof window === 'undefined') return snapshot
  const id = ++requestId
  try {
    const response = await requestService.get('/trading-status/')
    const data = await response.json()
    if (id !== requestId || !data) return snapshot
    publish(normalize(data))
  } catch (error) {
    if (id === requestId) {
      console.warn('site-control: trading-status read failed', error?.status || error)
    }
  }
  return snapshot
}

export function useSiteSnapshot({ poll = false, userId = null } = {}) {
  const [value, setValue] = useState(snapshot)

  useEffect(() => {
    let cancelled = false
    const listener = next => {
      if (!cancelled) setValue(next)
    }
    listeners.add(listener)
    setValue(snapshot)
    refreshSiteStatus()
    if (!poll) {
      return () => {
        cancelled = true
        listeners.delete(listener)
      }
    }
    let timer = window.setInterval(refreshSiteStatus, 10000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshSiteStatus()
        if (!timer) timer = window.setInterval(refreshSiteStatus, 10000)
      } else {
        window.clearInterval(timer)
        timer = null
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      listeners.delete(listener)
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [poll, userId])

  return value
}

export function useSiteControl(userId) {
  return useSiteSnapshot({ poll: true, userId: userId ?? null })
}
